import type { Face } from '@phi/jcore';
import 'vanilla-colorful';
import styles from './index.module.scss';
import { utility, Dialog } from '@phi/components';
import {
    add_to_palette,
    color_picker_position,
    hide_color_picker,
    selected_color,
    showing_color_picker,
} from '@features/color-picker/state';
import { defineComponent, h, nextTick, watch } from 'vue';
import {
    rgbToHex,
    set_color_of_selected_faces,
} from '@frontend/colours_materials_appearance';
import { filter, limit, map, repeat } from '@vaaas/fpts/iter';
import { uid_deselect_all_objects } from '@frontend/ui_display';
import { px } from '@phi/util/css';
import { pipe } from '@vaaas/fpts/function';
import { bind, isJust, maybe } from '@vaaas/fpts/option';
import { first } from '@vaaas/fpts/array';
import { color_parser } from '@features/color-picker/color-parser';
import { Picker } from '../picker';
import { Palette } from '../palette';
import { CurrentColorRow } from '../current-color-row';
import { ofKV } from '@vaaas/fpts/map';
import { get_from } from '@frontend/hashtable';
import { Uid_face } from '@frontend/graph_objects';
import { to_upper_case } from '@vaaas/fpts/string';

export const ColorPicker = defineComponent({
    data() {
        return {
            faces: [] as Face[],
            initial_face_colors: new Map as Map<Face, string>,
        };
    },

    methods: {
        on_document_click(e: PointerEvent) {
            if (!this.$el.contains(e.target)) {
                if (selected_color.value)
                    add_to_palette(selected_color.value);
                hide_color_picker();
            }
        },

        init_faces() {
            this.faces = uid_selected_faces.length
                ? uid_selected_faces.keys()
                : uid_faces.keys();
        },

        init_initial_face_colors() {
            this.initial_face_colors = pipe(
                this.faces,
                map(get_from(uid_faces)),
                filter(isJust<Uid_face>),
                ofKV((x: Uid_face) => x.id)(x => rgbToHex(x.color_r, x.color_g, x.color_b)),
            );
        },

        init_color() {
            selected_color.value = pipe(
                this.faces,
                first,
                bind(get_from(uid_faces)),
                maybe('', x => to_upper_case(rgbToHex(x.color_r, x.color_g, x.color_b))),
            );
        },

        colorise_faces(x: string) {
            const color = color_parser(x);
            if (color instanceof Error) return;
            selected_color.value = color;
            uid_deselect_all_objects();
            set_color_of_selected_faces(
                color,
                true,
                this.faces,
                Array.from(limit(this.faces.length)(repeat(color))),
            );
        },

        on_cancel() {
            const faces = Array.from(this.initial_face_colors.keys());
            const colors = Array.from(this.initial_face_colors.values());
            set_color_of_selected_faces(null, false, faces, colors);
            hide_color_picker();
        },
    },

    mounted() {
        watch(
            () => showing_color_picker.value,
            (value) => {
                if (value) {
                    document.addEventListener('pointerdown', this.on_document_click);
                    this.init_faces();
                    this.init_initial_face_colors();
                    nextTick(this.init_color);
                } else
                    document.removeEventListener('pointerdown', this.on_document_click);
            },
        );
    },

    render() {
        return h(Dialog, {
            show: showing_color_picker.value,
            'onUpdate:show': x => showing_color_picker.value = x,
            width: 'extra-narrow',
            close_label: 'Cancel',
            confirm_label: null,
            onClose: () => showing_color_picker.value = false,
            style: {
                position: 'absolute',
                left: px(color_picker_position.left),
                bottom: px(color_picker_position.bottom),
            },
        }, () => h('div', { class: utility.margin_gap_3 }, [
            h(Picker, {
                class: styles.picker,
                value: selected_color.value,
                'onUpdate:value': this.colorise_faces,
            }),

            h(CurrentColorRow, {
                value: selected_color.value || '#000000',
                'onUpdate:value': this.colorise_faces,
            }),

            h(Palette, {
                value: selected_color.value,
                'onUpdate:value': this.colorise_faces,
            }),
        ]));
    },
});
